import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { toast } from "sonner";
import { employeeService, getEmployeeById, uploadProfilePicture, deleteProfilePicture } from '../services/employee.service';
import { baseURL } from '@/lib/axios';
// import { documentService } from '../services/document.service'; // No longer needed - using unified API
import { 
  Users, Award, MapPin, 
  Briefcase, 
  BookOpen, 
  FileText, CreditCard, User, ChevronDown, ChevronRight,
  ArrowLeft
} from 'lucide-react';
import { Badge } from "@/components/ui/badge";

// Import separated components
import {
  PersonalInfoSection,
  EducationSection,
  FamilySection,
  AddressSection,
  PreviousEmployerSection
} from '../components/userProfile/Personal';

import {
  JobInfoSection,
  AgreementSection,
  CompensationSection,
  ResignationSection,
  InternshipSection
} from '../components/userProfile/Job';

import {
  BankInfoSection
} from '../components/userProfile/Banking';

import {
  PersonalDocSection,
  OfficialDocSection,
} from '../components/userProfile/Documents';

// Type definitions matching backend API response
interface ProfileData {
  _id: string;
  user: {
    _id: string;
  email: string;
    firstName: string;
    lastName: string;
  phone: string;
    employeeId: string;
    isActive: boolean;
    roles: Array<{
      _id: string;
      name: string;
      description: string;
    }>;
  };
  department: {
    _id: string;
    name: string;
  };
  salary: number;
  profilePicture?: string;
  jobInfo: {
    joiningDate: string;
  location: string;
    jobTitle: string;
    category: string;
    reportingManager: string[];
    lineManager: string[];
    lineManager2: string[];
    employmentStatus: string;
    probationPeriod: number;
    probationEndDate: string;
    feedbackRemainderDays: number;
    notifyDay: number;
    note: string;
  };
  agreementInfo: Array<{
    startDate: string;
    endDate: string;
    periodInMonths: string;
    notifyPeriodInDays: string;
    note: string;
  }>;
  compensationInfo: Array<{
    startDate: string;
    endDate: string;
    periodInMonths: string;
    previousSalary: string;
    incrementPercentage: string;
    newSalary: string;
    payFrequency: string;
    noticePeriodInDays: string;
    notifyPeriodInDays: string;
    monthlyBonus: string;
    salaryMode: string;
    note: string;
  }>;
  personal?: {
    personalInfo?: {
      displayName: string;
      middleName: string;
      gender: string;
      dateOfBirth: string;
      maritalStatus: string;
      nationality: string;
      bloodGroup: string;
      placeOfBirth: string;
      companyName: string;
   
    };
    familyInfo?: {
      familyMembers: Array<{
        name: string;
        relationship: string;
        dateOfBirth: string;
        occupation: string;
        mobile: string;
        aadhaar: string;
      }>;
    };
    addressInfo?: {
      currentAddress1: string;
      currentAddress2: string;
      currentCity: string;
      currentCountry: string;
      currentState: string;
      currentPinCode: string;
      sameAsCurrentAddress: boolean;
      permanentAddress1: string;
      permanentAddress2: string;
      permanentCity: string;
      permanentCountry: string;
      permanentState: string;
      permanentPinCode: string;
    };
    educationEntries?: Array<{
      degree: string;
      collegeName: string;
      specialization: string;
      startDate: Date;
      endDate: Date;
    }>;
    experienceDetails?: {
      experienceType: 'fresher' | 'experience';
      totalYears: number;
      totalMonths: number;
      previousEmployers: Array<{
        companyName: string;
        department?: string;
        jobTitle: string;
        startDate: Date;
        endDate: Date;
      }>;
    };
  };
  resignationInfo: {
  resignationType: string;
  reason: string;
  noticeGivenOn: string;
  lastWorkingDay: string;
  note: string;
  revokeReason: string;
  remark: string;
  revokedOn: string;
  status: string;
  };
  internshipInfo: Array<{
    startDate: string;
    endDate: string;
    length: string;
    reduceExtended: string;
  }>;
  banking: Array<{
    countryName: string;
    accountHolderName: string;
    bankName: string;
    bankBranch: string;
    accountNumber: string;
    accountType: string;
    attachments: string[];
    ifscCode: string;
    ibanCode: string;
    bankCode: string;
    branchCode: string;
    swiftCode: string;
    status: string;
  }>;
  documents?: {
    personalDocuments?: Array<{
      id: string;
      name: string;
      number: string;
      fullName: string;
      attachments: string[];
      expiredOn: string;
      status: 'active' | 'expired' | 'pending' | 'draft';
      type: 'personal' | 'official';
      description?: string;
      uploadedOn: string;
      lastModified: string;
    }>;
    officialDocuments?: Array<{
      id: string;
      name: string;
      number: string;
      fullName: string;
      attachments: string[];
      expiredOn: string;
      status: 'active' | 'expired' | 'pending' | 'draft';
      type: 'personal' | 'official';
      description?: string;
      uploadedOn: string;
      lastModified: string;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}


// These interfaces are now defined in the ProfileData interface above

export default function UserProfile() {
  const { user, hasPermission } = useAuthStore();
  const { employeeId } = useParams<{ employeeId?: string }>();
  const navigate = useNavigate();
  
  // Check if user can edit - either own profile or has employee edit permissions
  const canEdit = !employeeId || hasPermission('EMPLOYEES_EDIT') || hasPermission('EMPLOYEES_CREATE');
  
  // Check if user can view - either own profile or has any employee permission
  const canView = !employeeId || hasPermission('EMPLOYEES_VIEW') || hasPermission('EMPLOYEES_EDIT') || hasPermission('EMPLOYEES_CREATE');
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [isUploadingProfilePicture, setIsUploadingProfilePicture] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
  const [selectedMainSection, setSelectedMainSection] = useState("personal");
  const [selectedSubSection, setSelectedSubSection] = useState("personal-info");
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    job: false,
    banking: false,
    documents: false
  });
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Section-specific edit states - allow editing if user has permission
  const [sectionEditStates, setSectionEditStates] = useState({
    personalInfo: canEdit,
    education: canEdit,
    family: canEdit,
    address: canEdit,
    previousEmployer: canEdit,
    jobInfo: canEdit,
    agreement: canEdit,
    compensation: canEdit,
    resignation: canEdit,
    internship: canEdit,
    bankInfo: canEdit,
    personalDoc: canEdit,
    officialDoc: canEdit,
  });
  
const [experienceType, setExperienceType] = useState<'fresher' | 'experience'>('fresher');
  const [experienceDetails, setExperienceDetails] = useState<{
    experienceType: 'fresher' | 'experience';
    totalYears: number;
    totalMonths: number;
    previousEmployers: Array<{
      companyName: string;
      department?: string;
      jobTitle: string;
      startDate: Date;
      endDate: Date;
    }>;
  }>({
    experienceType: 'fresher',
    totalYears: 0,
    totalMonths: 0,
    previousEmployers: [
      {
        companyName: '',
        department: '',
        jobTitle: '',
        startDate: new Date(),
        endDate: new Date()
      }
    ]
  });

  // Main profile data from API
  const [profileData, setProfileData] = useState<ProfileData | null>(null);

  // Load profile data from API
  useEffect(() => {
    const loadProfileData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check if user has permission to view employee profiles
        if (employeeId && !canView) {
          setError('You do not have permission to view this employee profile');
          setLoading(false);
          return;
        }
        
        // Check if we're viewing another employee's profile
        let data;
        if (employeeId) {
          // Viewing another employee's profile
          data = await getEmployeeById(employeeId);
        } else {
          // Viewing own profile
          data = await employeeService.getMyProfile();
        }
        
        // Cast the data to ProfileData type since we know the API returns the correct structure
        setProfileData(data as unknown as ProfileData);
        
        
        // Set profile image if available
        if (data.profilePicture) {
          // Construct full URL for profile picture
          const backendBaseUrl = baseURL.replace('/api/v1', '');
          const fullImageUrl = data.profilePicture.startsWith('http') 
            ? data.profilePicture 
            : `${backendBaseUrl}${data.profilePicture}`;
          setProfileImage(fullImageUrl);
        } else {
          setProfileImage(null);
        }
        
        // Initialize experience data from API response
        if (data.personal?.experienceDetails) {
          setExperienceType(data.personal.experienceDetails.experienceType || 'fresher');
          setExperienceDetails({
            experienceType: data.personal.experienceDetails.experienceType || 'fresher',
            totalYears: data.personal.experienceDetails.totalYears || 0,
            totalMonths: data.personal.experienceDetails.totalMonths || 0,
            previousEmployers: data.personal.experienceDetails.previousEmployers || []
          });
        }
      } catch (err) {
        console.error('Error loading profile data:', err);
        setError('Failed to load profile data');
        toast.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    if (user || employeeId) {
      loadProfileData();
    }
  }, [user, employeeId, canView]);

  // Refresh profile data from API
  const refreshProfileData = async () => {
    try {
      let data;
      if (employeeId) {
        // Viewing another employee's profile
        data = await getEmployeeById(employeeId);
      } else {
        // Viewing own profile
        data = await employeeService.getMyProfile();
      }
      setProfileData(data as unknown as ProfileData);
      
      // Set profile image if available
      if (data.profilePicture) {
        // Construct full URL for profile picture
        const backendBaseUrl = baseURL.replace('/api/v1', '');
        const fullImageUrl = data.profilePicture.startsWith('http') 
          ? data.profilePicture 
          : `${backendBaseUrl}${data.profilePicture}`;
        setProfileImage(fullImageUrl);
      } else {
        setProfileImage(null);
      }
      
      // Update experience data if it exists
      if (data.personal?.experienceDetails) {
        setExperienceType(data.personal.experienceDetails.experienceType || 'fresher');
        setExperienceDetails({
          experienceType: data.personal.experienceDetails.experienceType || 'fresher',
          totalYears: data.personal.experienceDetails.totalYears || 0,
          totalMonths: data.personal.experienceDetails.totalMonths || 0,
          previousEmployers: data.personal.experienceDetails.previousEmployers || []
        });
      }
    } catch (err) {
      console.error('Error refreshing profile data:', err);
    }
  };

  // Handle profile picture upload
  const handleProfilePictureUpload = async (file: File) => {
    if (!profileData || !canEdit) {
      toast.error('You do not have permission to edit this profile');
      return;
    }

    try {
      setIsUploadingProfilePicture(true);
      
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error('File size must be less than 5MB');
        return;
      }

      await uploadProfilePicture(profileData._id, file);
      
      // Refresh profile data to get updated profile picture
      await refreshProfileData();
      
      // Show upload confirmation popup
      setShowUploadConfirmation(true);
      
      toast.success('Profile picture updated successfully');
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error('Failed to upload profile picture');
    } finally {
      setIsUploadingProfilePicture(false);
    }
  };

  // Handle profile picture deletion confirmation
  const handleProfilePictureDeleteClick = () => {
    if (!profileData || !canEdit) {
      toast.error('You do not have permission to edit this profile');
      return;
    }
    setShowDeleteConfirmation(true);
  };

  // Handle profile picture deletion
  const handleProfilePictureDelete = async () => {
    if (!profileData || !canEdit) {
      toast.error('You do not have permission to edit this profile');
      return;
    }

    try {
      setIsUploadingProfilePicture(true);
      
      await deleteProfilePicture(profileData._id);
      
      // Refresh profile data to get updated profile picture
      await refreshProfileData();
      
      toast.success('Profile picture deleted successfully');
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      toast.error('Failed to delete profile picture');
    } finally {
      setIsUploadingProfilePicture(false);
      setShowDeleteConfirmation(false);
    }
  };

  // Update profile data - unified approach for all sections
  const updateProfileData = async (updateData: any, files?: File[]) => {
    if (!profileData) return;
    
    // Check if user has permission to edit
    if (!canEdit) {
      toast.error('You do not have permission to edit this profile');
      return;
    }
    
    try {
      // Use the unified employee update endpoint for all sections
      await employeeService.updateProfile(profileData._id, updateData, files);
      
      // Refresh profile data from API to get the latest data from database
      await refreshProfileData();
      
      toast.success('Profile updated successfully');
    } catch (err) {
      console.error('Error updating profile:', err);
      console.error('Error details:', err);
      toast.error('Failed to update profile');
    }
  };

  // Toggle edit mode for sections (currently unused but kept for future use)
  // const toggleEditMode = (section: string) => {
  //   setSectionEditStates(prev => ({
  //     ...prev,
  //     [section]: !prev[section as keyof typeof prev]
  //   }));
  // };

  // Save section data
  // Special function to handle document data merging - now uses unified API
  const saveDocumentData = async (documentType: 'personalDocuments' | 'officialDocuments', documents: any[], files?: File[]) => {
    // Check if user has permission to edit
    if (!canEdit) {
      toast.error('You do not have permission to edit this profile');
      return;
    }
    
    try {

      // Get existing document data (flattened structure)
      const existingDocuments = profileData?.documents || {};
      
      // Create merged document data (flattened structure)
      const mergedDocumentData = {
        ...existingDocuments,
        [documentType]: documents
      };

      // Use the unified updateProfileData method with flattened structure
      const updateData = {
        documents: mergedDocumentData
      };

      await updateProfileData(updateData, files);
      
      toast.success(`${documentType === 'personalDocuments' ? 'Personal' : 'Official'} documents updated successfully`);
    } catch (error) {
      console.error('Error saving document data:', error);
      toast.error('Failed to save documents');
    }
  };

  const saveSectionData = async (section: string, data: any, files?: File[]) => {
    // Check if user has permission to edit
    if (!canEdit) {
      toast.error('You do not have permission to edit this profile');
      return;
    }
    
    try {
      // Special handling for banking data - it should be at root level
      if (section === 'banking') {
        const updateData = {
          banking: data
        };
        
        await updateProfileData(updateData, files);
        setSectionEditStates(prev => ({
          ...prev,
          [section]: false
        }));
        return;
      }

      // Special handling for resignation data - it should be at root level
      if (section === 'resignationInfo') {
        const updateData = {
          resignationInfo: data
        };
        
        await updateProfileData(updateData, files);
        setSectionEditStates(prev => ({
          ...prev,
          [section]: false
        }));
        return;
      }

      // Special handling for internship data - it should be at root level
      if (section === 'internshipInfo') {
        const updateData = {
          internshipInfo: data
        };
        
        await updateProfileData(updateData, files);
        setSectionEditStates(prev => ({
          ...prev,
          [section]: false
        }));
        return;
      }

      // Special handling for array-based sections (agreementInfo, compensationInfo)
      if (section === 'agreementInfo' || section === 'compensationInfo') {
        const updateData = {
          [section]: data
        };
        
        await updateProfileData(updateData, files);
        setSectionEditStates(prev => ({
          ...prev,
          [section]: false
        }));
        return;
      }

      // Map section names to the correct nested structure
      const sectionMapping: { [key: string]: string } = {
        'personalInfo': 'personal.personalInfo',
        'familyInfo': 'personal.familyInfo', 
        'addressInfo': 'personal.addressInfo',
        'educationEntries': 'personal.educationEntries',
        'experienceDetails': 'personal.experienceDetails',
        'documents': 'documents',
        'resignationInfo': 'resignationInfo',
        'internshipInfo': 'internshipInfo'
      };

      const nestedPath = sectionMapping[section] || section;
      
      // Create the nested update object
      const updateData: any = {};
      const pathParts = nestedPath.split('.');
      let current: any = updateData;
      
      for (let i = 0; i < pathParts.length - 1; i++) {
        current[pathParts[i]] = {};
        current = current[pathParts[i]];
      }
      current[pathParts[pathParts.length - 1]] = data;

      await updateProfileData(updateData, files);
      setSectionEditStates(prev => ({
        ...prev,
        [section]: false
      }));
    } catch (err) {
      console.error(`Error saving ${section}:`, err);
      toast.error(`Failed to save ${section} data`);
    }
  };
  


  // Render content based on selected section
  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading profile data...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-600 mb-4">Error loading profile: {error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    if (!profileData) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-gray-600 mb-4">No profile data found</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }

    // Transform profile data for backward compatibility with existing components
    const transformedProfileData = {
      name: profileData.user?.firstName && profileData.user?.lastName 
        ? `${profileData.user.firstName} ${profileData.user.lastName}` 
        : profileData.user?.email || '',
      email: profileData.user?.email || '',
      phone: profileData.user?.phone || '',
      location: profileData.jobInfo?.location || '',
      role: profileData.jobInfo?.jobTitle || '',
      department: profileData.department?.name || '',
      designation: profileData.jobInfo?.jobTitle || '',
      joiningDate: profileData.jobInfo?.joiningDate || '',
      employeeId: profileData.user?.employeeId || '',
      reportingManager: Array.isArray(profileData.jobInfo?.reportingManager) 
        ? profileData.jobInfo.reportingManager.map(item => 
            typeof item === 'string' ? item : (item as any)?.title || (item as any)?.name || ''
          ).filter(Boolean).join(', ') 
        : profileData.jobInfo?.reportingManager || '',
     
      // Basic Info
      companyName: profileData.personal?.personalInfo?.companyName || '',
      uniqueId: profileData.user?.employeeId || '',
      middleName: profileData.personal?.personalInfo?.middleName || '',
      gender: profileData.personal?.personalInfo?.gender || '',
      placeOfBirth: profileData.personal?.personalInfo?.placeOfBirth || '',
      dateOfBirth: profileData.personal?.personalInfo?.dateOfBirth || '',
      maritalStatus: profileData.personal?.personalInfo?.maritalStatus || '',
      nationality: profileData.personal?.personalInfo?.nationality || '',
      bloodGroup: profileData.personal?.personalInfo?.bloodGroup || '',
      displayName: profileData.personal?.personalInfo?.displayName || '',
      firstName: profileData.user?.firstName || '',
      lastName: profileData.user?.lastName || '',
      // Address
      currentAddress1: profileData.personal?.addressInfo?.currentAddress1 || '',
      currentAddress2: profileData.personal?.addressInfo?.currentAddress2 || '',
      currentCity: profileData.personal?.addressInfo?.currentCity || '',
      currentCountry: profileData.personal?.addressInfo?.currentCountry || '',
      currentState: profileData.personal?.addressInfo?.currentState || '',
      currentPinCode: profileData.personal?.addressInfo?.currentPinCode || '',
      sameAsCurrentAddress: profileData.personal?.addressInfo?.sameAsCurrentAddress || false,
      permanentAddress1: profileData.personal?.addressInfo?.permanentAddress1 || '',
      permanentAddress2: profileData.personal?.addressInfo?.permanentAddress2 || '',
      permanentCity: profileData.personal?.addressInfo?.permanentCity || '',
      permanentCountry: profileData.personal?.addressInfo?.permanentCountry || '',
      permanentState: profileData.personal?.addressInfo?.permanentState || '',
      permanentPinCode: profileData.personal?.addressInfo?.permanentPinCode || '',
      educationEntries: profileData.personal?.educationEntries || [],
      familyMembers: profileData.personal?.familyInfo?.familyMembers || [],
      // Add personal object for nested access
      personal: {
        personalInfo: profileData.personal?.personalInfo,
        familyInfo: profileData.personal?.familyInfo,
        addressInfo: profileData.personal?.addressInfo,
        educationEntries: profileData.personal?.educationEntries || [],
        experienceDetails: profileData.personal?.experienceDetails
      }
    };

    // Debug logging removed - issue was in backend nested object handling

    switch (selectedSubSection) {
      case "personal-info":
        return <PersonalInfoSection 
          profileData={transformedProfileData} 
          setProfileData={(data) => saveSectionData('personalInfo', data)} 
          isEditing={sectionEditStates.personalInfo}
        />;
      case "education":
        return <EducationSection 
          profileData={transformedProfileData} 
          setProfileData={(data) => saveSectionData('educationEntries', data)} 
          isEditing={sectionEditStates.education}
        />;
      case "family":
        return <FamilySection 
          profileData={transformedProfileData} 
          setProfileData={(data) => saveSectionData('familyInfo', data)} 
          isEditing={sectionEditStates.family}
        />;
      case "address":
        return <AddressSection 
          profileData={transformedProfileData} 
          setProfileData={(data) => saveSectionData('addressInfo', data)} 
          isEditing={sectionEditStates.address}
        />;
      case "previous-employer":
        return <PreviousEmployerSection 
          experienceType={experienceType} 
          setExperienceType={setExperienceType} 
          experienceDetails={experienceDetails} 
          setExperienceDetails={setExperienceDetails} 
          isEditing={sectionEditStates.previousEmployer}
          setProfileData={(data) => saveSectionData('experienceDetails', data)}
        />;
      case "job-info":
        return <JobInfoSection 
          jobData={{
            effectiveDate: profileData.jobInfo?.joiningDate || '',
            location: profileData.jobInfo?.location || '',
            jobTitle: profileData.jobInfo?.jobTitle || '',
            category: profileData.jobInfo?.category || '',
            reportingManager: profileData.jobInfo?.reportingManager || '',
            lineManager: profileData.jobInfo?.lineManager || '',
            lineManager2: profileData.jobInfo?.lineManager2 || '',
            employmentStatus: profileData.jobInfo?.employmentStatus || '',
            probationPeriod: profileData.jobInfo?.probationPeriod?.toString() || '',
            probationEndDate: profileData.jobInfo?.probationEndDate || '',
            feedbackRemainderDays: profileData.jobInfo?.feedbackRemainderDays?.toString() || '',
            note: profileData.jobInfo?.note || ''
          }} 
          setJobData={(data) => saveSectionData('jobInfo', data)} 
          isEditing={sectionEditStates.jobInfo}
        />;
      case "agreement":
        return <AgreementSection 
          agreementData={profileData.agreementInfo || []} 
          setAgreementData={(data) => saveSectionData('agreementInfo', data)} 
          isEditing={sectionEditStates.agreement}
        />;
      case "compensation":
        return <CompensationSection 
          compensationData={profileData.compensationInfo || []} 
          setCompensationData={(data) => saveSectionData('compensationInfo', data)} 
          isEditing={sectionEditStates.compensation}
        />;
      case "resignation":
        return <ResignationSection 
          resignationData={profileData.resignationInfo || {
            resignationType: '',
            reason: '',
            noticeGivenOn: '',
            lastWorkingDay: '',
            note: '',
            revokeReason: '',
            remark: '',
            revokedOn: '',
            status: ''
          }} 
          setResignationData={(data) => saveSectionData('resignationInfo', data)} 
          isEditing={sectionEditStates.resignation}
          employeeData={profileData}
        />;
      case "internship":
        return <InternshipSection 
          internshipData={Array.isArray(profileData.internshipInfo) ? profileData.internshipInfo : (profileData.internshipInfo ? [profileData.internshipInfo] : [])} 
          setInternshipData={(data) => saveSectionData('internshipInfo', data)} 
          isEditing={sectionEditStates.internship}
        />;
      case "bank-info":
        return <BankInfoSection 
          bankingData={profileData.banking || []} 
          setBankingData={(data, files) => saveSectionData('banking', data, files)} 
          isEditing={sectionEditStates.bankInfo}
        />;
        case "personal-doc":
        return <PersonalDocSection 
          documents={profileData.documents?.personalDocuments || []}
          onSave={(documents, files) => saveDocumentData('personalDocuments', documents, files)}
        />;
      case "official-doc":
        const officialDocs = profileData.documents?.officialDocuments || [];
        return <OfficialDocSection 
          documents={officialDocs}
          onSave={(documents, files) => saveDocumentData('officialDocuments', documents, files)}
        />;
      default:
        return <PersonalInfoSection 
          profileData={transformedProfileData} 
          setProfileData={(data) => saveSectionData('personalInfo', data)} 
          isEditing={sectionEditStates.personalInfo}
        />;
    }
  };

  // Handle tab click

  // Enhanced dropdown click handler

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section as keyof typeof prev]
    }));
    setSelectedMainSection(section);
  };

  // Toggle edit state for a specific section

  // Save section changes

  return (
    <div className="min-h-screen ">
      <div className="flex h-screen">
        {/* Left Sidebar */}
        <div className="w-80 bg-white shadow-md border border-gray-200 rounded-xl flex flex-col mt-6 mb-10 overflow-hidden">
          {/* Back Button - Only show when viewing another employee's profile */}
          {employeeId && (
            <div className="flex-shrink-0 bg-white border-b border-gray-200 rounded-t-xl">
              <div className="p-4">
                <button
                  onClick={() => navigate('/employees')}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Employees
                </button>
              </div>
            </div>
          )}
          
          {/* Profile Summary - Fixed at top */}
          <div className="flex-shrink-0 bg-white border-b border-gray-200 rounded-t-xl">
            <div className="p-5">
              <div className="flex flex-col items-center">
                <div className="relative mb-2">
                  <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        className="h-full w-full rounded-full object-cover"
                      />
                    ) : (
                      <Users className="h-12 w-12 text-white" />
                    )}
                  </div>
                  {canEdit && !profileImage && (
                    <div className="absolute -bottom-1 -right-1">
                      <div className="relative">
                        <input
                          ref={(input) => {
                            if (input) {
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0];
                                if (file) {
                                  handleProfilePictureUpload(file);
                                  // Reset the input so the same file can be selected again
                                  input.value = '';
                                }
                              };
                            }
                          }}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          disabled={isUploadingProfilePicture}
                        />
                        <button
                          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow-lg transition-colors disabled:opacity-50"
                          disabled={isUploadingProfilePicture}
                          title="Upload profile picture"
                          onClick={(e) => {
                            e.preventDefault();
                            const input = e.currentTarget.parentElement?.querySelector('input[type="file"]') as HTMLInputElement;
                            input?.click();
                          }}
                        >
                          {isUploadingProfilePicture ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  {canEdit && profileImage && (
                    <div className="absolute -top-1 -right-1">
                      <button
                        onClick={handleProfilePictureDeleteClick}
                        className="bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-lg transition-colors disabled:opacity-50"
                        disabled={isUploadingProfilePicture}
                        title="Delete profile picture"
                      >
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-1">
                  {profileData?.user?.firstName && profileData?.user?.lastName 
                    ? `${profileData.user.firstName} ${profileData.user.lastName}` 
                    : profileData?.user?.email || 'Loading...'}
                </h2>
                <p className="text-sm text-gray-600 mb-3">
                  {(() => {
                    const jobTitle = profileData?.jobInfo?.jobTitle;
                    if (jobTitle) {
                      if (typeof jobTitle === "string") return jobTitle;
                      if (typeof jobTitle === "object" && 'title' in jobTitle) return (jobTitle as any).title;
                    }
                    return profileData?.personal?.personalInfo?.displayName || 'Employee';
                  })()}
                </p>
                <div className="flex gap-2 mb-4">
                  <Badge variant="secondary" className="bg-blue-50 text-blue-600 rounded-full px-3 py-1">
                    {profileData?.department?.name || 'Department'}
                  </Badge>
                  <Badge variant="secondary" className="bg-green-50 text-green-600 rounded-full px-3 py-1">
                    {profileData?.user?.employeeId || 'ID'}
                  </Badge>
                </div>

              </div>
            </div>
          </div>

          {/* Navigation Menu - Scrollable with fixed height */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4">
              <div className="space-y-2">
                {/* Personal Section */}
                <div>
                  <button
                    onClick={() => toggleSection("personal")}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Personal
                    </div>
                    {expandedSections.personal ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {expandedSections.personal && (
                    <div className="space-y-1 ml-6 mt-2">
                      <button
                        onClick={() => {
                          setSelectedMainSection("personal");
                          setSelectedSubSection("personal-info");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "personal" && selectedSubSection === "personal-info"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Personal Information
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMainSection("personal");
                          setSelectedSubSection("education");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "personal" && selectedSubSection === "education"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <BookOpen className="h-4 w-4" />
                          Education
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMainSection("personal");
                          setSelectedSubSection("family");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "personal" && selectedSubSection === "family"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Family
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMainSection("personal");
                          setSelectedSubSection("address");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "personal" && selectedSubSection === "address"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          Address
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMainSection("personal");
                          setSelectedSubSection("previous-employer");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "personal" && selectedSubSection === "previous-employer"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4" />
                          Previous Employer
                        </div>
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Job Section */}
                <div className="mt-4">
                  <button
                    onClick={() => toggleSection("job")}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Job
                    </div>
                    {expandedSections.job ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {expandedSections.job && (
                    <div className="space-y-1 ml-6 mt-2">
                      <button
                        onClick={() => {
                          setSelectedMainSection("job");
                          setSelectedSubSection("job-info");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "job" && selectedSubSection === "job-info"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4" />
                          Job Information
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMainSection("job");
                          setSelectedSubSection("agreement");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "job" && selectedSubSection === "agreement"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Agreement
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMainSection("job");
                          setSelectedSubSection("compensation");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "job" && selectedSubSection === "compensation"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Compensation
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMainSection("job");
                          setSelectedSubSection("resignation");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "job" && selectedSubSection === "resignation"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Employee Resignation
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMainSection("job");
                          setSelectedSubSection("internship");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "job" && selectedSubSection === "internship"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Award className="h-4 w-4" />
                          Internship/Probation
                        </div>
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Banking Section */}
                <div className="mt-4">
                  <button
                    onClick={() => toggleSection("banking")}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Banking
                    </div>
                    {expandedSections.banking ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {expandedSections.banking && (
                    <div className="space-y-1 ml-6 mt-2">
                      <button
                        onClick={() => {
                          setSelectedMainSection("banking");
                          setSelectedSubSection("bank-info");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "banking" && selectedSubSection === "bank-info"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Bank Information
                        </div>
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Documents Section */}
                <div className="mt-4">
                  <button
                    onClick={() => toggleSection("documents")}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Documents
                    </div>
                    {expandedSections.documents ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  {expandedSections.documents && (
                    <div className="space-y-1 ml-6 mt-2">
                      <button
                        onClick={() => {
                          setSelectedMainSection("documents");
                          setSelectedSubSection("personal-doc");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "documents" && selectedSubSection === "personal-doc"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Personal Document
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedMainSection("documents");
                          setSelectedSubSection("official-doc");
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedMainSection === "documents" && selectedSubSection === "official-doc"
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Official Document
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content Area */}
        <div className="flex-1 overflow-hidden">
          {/* Content */}
          <div className="h-full overflow-y-auto p-6 pr-0">
            <div className="bg-white shadow-md border border-gray-200 rounded-xl overflow-hidden">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Profile Picture</h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete the profile picture? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                disabled={isUploadingProfilePicture}
              >
                Cancel
              </button>
              <button
                onClick={handleProfilePictureDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50"
                disabled={isUploadingProfilePicture}
              >
                {isUploadingProfilePicture ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Confirmation Dialog */}
      {showUploadConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Profile Picture Updated</h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-500">
                Your profile picture has been successfully updated.
              </p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowUploadConfirmation(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}